import React, { useState, useEffect, useMemo } from 'react';
import { Check, AlertTriangle, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency, formatDate, previewSchedule, calcContractValue, addMonths } from '../utils/format';
import api from '../services/api';

const FREQ_LABELS = { monthly: 'Mensuel', quarterly: 'Trimestriel', annual: 'Annuel' };
const INSTALLMENT_OPTIONS = ['1', '2', '3', '4', '6', '12', 'custom'];
const TYPE_CFG = {
  deposit: { label: 'Acompte', cls: 'bg-amber-50 text-amber-700' },
  initial: { label: 'Initial', cls: 'bg-indigo-50 text-indigo-700' },
  recurring: { label: 'Récurrent', cls: 'bg-purple-50 text-purple-700' },
};

const today = new Date().toISOString().split('T')[0];

export default function CreatePaymentPlanModal({ deal, open, onClose, onCreated }) {
  const [offers, setOffers] = useState([]);
  const [installmentPreset, setInstallmentPreset] = useState('1');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    offer_id: '',
    has_deposit: false,
    deposit_type: 'fixed',
    deposit_amount: 0,
    deposit_due_date: today,
    has_initial: true,
    initial_amount: deal?.value || 0,
    initial_installments: 1,
    initial_start_date: today,
    has_recurring: false,
    recurring_amount: 0,
    recurring_frequency: 'monthly',
    recurring_count: 12,
    recurring_start_date: addMonths(today, 1),
    notes: '',
  }));

  useEffect(() => {
    api.get('/offers').then(r => setOffers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && deal) {
      setForm(f => ({ ...f, initial_amount: deal.value || 0 }));
      setInstallmentPreset('1');
    }
  }, [open, deal]);

  const applyOffer = (offerId) => {
    if (offerId === '__none__') { setForm(f => ({ ...f, offer_id: '' })); return; }
    const offer = offers.find(o => o.id === offerId);
    if (!offer) { setForm(f => ({ ...f, offer_id: '' })); return; }
    const newPreset = INSTALLMENT_OPTIONS.includes(String(offer.initial_installments || 1))
      ? String(offer.initial_installments || 1)
      : 'custom';
    setInstallmentPreset(newPreset);
    setForm(f => ({
      ...f,
      offer_id: offerId,
      has_deposit: !!(offer.deposit_value),
      deposit_type: offer.deposit_type || 'fixed',
      deposit_amount: offer.deposit_value || 0,
      has_initial: true,
      initial_amount: offer.initial_amount || deal?.value || 0,
      initial_installments: offer.initial_installments || 1,
      has_recurring: offer.has_recurring || false,
      recurring_amount: offer.recurring_amount || 0,
      recurring_frequency: offer.recurring_frequency || 'monthly',
      recurring_count: offer.recurring_count || 12,
    }));
  };

  // Compute actual deposit in € for preview
  const actualDepositAmount = useMemo(() => {
    if (!form.has_deposit) return 0;
    if (form.deposit_type === 'percent') {
      return Math.round(form.initial_amount * (form.deposit_amount || 0) / 100 * 100) / 100;
    }
    return form.deposit_amount || 0;
  }, [form.has_deposit, form.deposit_type, form.deposit_amount, form.initial_amount]);

  const previewForm = useMemo(() => ({
    ...form,
    deposit_amount: actualDepositAmount,
    deposit_type: 'fixed',
  }), [form, actualDepositAmount]);

  const previewLines = useMemo(() => previewSchedule(previewForm), [previewForm]);

  const contractValue = useMemo(() =>
    calcContractValue(form.initial_amount, form.has_recurring, form.recurring_amount, form.recurring_count),
    [form.initial_amount, form.has_recurring, form.recurring_amount, form.recurring_count]
  );

  const scheduleTotal = useMemo(() =>
    Math.round(previewLines.reduce((s, l) => s + l.amount, 0) * 100) / 100,
    [previewLines]
  );

  const validationError = useMemo(() => {
    if (previewLines.length === 0) return null;
    const diff = Math.abs(Math.round((scheduleTotal - contractValue) * 100));
    if (diff > 1) {
      return `Écart de ${formatCurrency(Math.abs(scheduleTotal - contractValue))} entre l'échéancier (${formatCurrency(scheduleTotal)}) et la valeur contractuelle (${formatCurrency(contractValue)}).`;
    }
    return null;
  }, [scheduleTotal, contractValue, previewLines]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleInstallmentChange = (val) => {
    setInstallmentPreset(val);
    if (val !== 'custom') setField('initial_installments', parseInt(val));
  };

  const handleSubmit = async () => {
    if (validationError) { toast.error('Corrigez l\'écart avant de valider'); return; }
    if (!form.has_initial && !form.has_recurring) { toast.error('Activez au moins un type de paiement'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        deposit_amount: actualDepositAmount,
        deposit_type: 'fixed',
        deal_id: deal.id,
        initial_amount: parseFloat(form.initial_amount) || 0,
        recurring_amount: parseFloat(form.recurring_amount) || 0,
        recurring_count: parseInt(form.recurring_count) || 1,
        initial_installments: parseInt(form.initial_installments) || 1,
      };
      const res = await api.post('/payment-plans', payload);
      toast.success('Plan de paiement créé avec succès');
      onCreated(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la création'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            Créer l'échéancier de paiement
          </DialogTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Devis : <strong>{deal?.title}</strong> · {formatCurrency(deal?.value)}</p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Offer selector */}
          {offers.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
              <Label className="text-sm font-medium mb-1.5 block">Modèle d'offre (optionnel)</Label>
              <Select value={form.offer_id} onValueChange={applyOffer}>
                <SelectTrigger data-testid="offer-selector">
                  <SelectValue placeholder="Sélectionner un modèle..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Aucun modèle —</SelectItem>
                  {offers.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400 mt-1">Le modèle prérempli les champs, vous pouvez les modifier.</p>
            </div>
          )}

          {/* Deposit section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Acompte</p>
                <p className="text-xs text-gray-400">Optionnel — fait partie du paiement initial</p>
              </div>
              <button
                onClick={() => setField('has_deposit', !form.has_deposit)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.has_deposit ? 'bg-[#1B99E5]' : 'bg-gray-200 dark:bg-gray-700'}`}
                data-testid="toggle-deposit"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.has_deposit ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {form.has_deposit && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-medium mb-1 block">Type</Label>
                  <Select value={form.deposit_type} onValueChange={v => setField('deposit_type', v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Montant fixe (€)</SelectItem>
                      <SelectItem value="percent">Pourcentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">{form.deposit_type === 'percent' ? 'Pourcentage (%)' : 'Montant (€)'}</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.deposit_amount}
                    onChange={e => setField('deposit_amount', e.target.value)}
                    className="h-8 text-sm"
                    data-testid="deposit-amount"
                  />
                  {form.deposit_type === 'percent' && (
                    <p className="text-xs text-gray-400 mt-0.5">= {formatCurrency(actualDepositAmount)}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Date d'échéance</Label>
                  <Input type="date" value={form.deposit_due_date} onChange={e => setField('deposit_due_date', e.target.value)} className="h-8 text-sm" />
                </div>
              </div>
            )}
          </div>

          {/* Initial payment section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Paiement initial</p>
                <p className="text-xs text-gray-400">Montant total initial (acompte inclus)</p>
              </div>
              <button
                onClick={() => setField('has_initial', !form.has_initial)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.has_initial ? 'bg-[#1B99E5]' : 'bg-gray-200 dark:bg-gray-700'}`}
                data-testid="toggle-initial"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.has_initial ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {form.has_initial && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-medium mb-1 block">Montant total initial (€)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.initial_amount}
                    onChange={e => setField('initial_amount', e.target.value)}
                    className="h-8 text-sm"
                    data-testid="initial-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Nombre d'échéances</Label>
                  <Select value={installmentPreset} onValueChange={handleInstallmentChange}>
                    <SelectTrigger className="h-8 text-sm" data-testid="installment-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['1', '2', '3', '4', '6', '12'].map(n => <SelectItem key={n} value={n}>{n === '1' ? '1 (tout en une fois)' : n}</SelectItem>)}
                      <SelectItem value="custom">Personnalisé</SelectItem>
                    </SelectContent>
                  </Select>
                  {installmentPreset === 'custom' && (
                    <Input
                      type="number" min="1" max="999"
                      value={form.initial_installments}
                      onChange={e => setField('initial_installments', parseInt(e.target.value) || 1)}
                      className="h-8 text-sm mt-1"
                      placeholder="Nombre d'échéances"
                      data-testid="installment-custom"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Première date</Label>
                  <Input type="date" value={form.initial_start_date} onChange={e => setField('initial_start_date', e.target.value)} className="h-8 text-sm" />
                </div>
              </div>
            )}
          </div>

          {/* Recurring payment section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Paiement récurrent</p>
                <p className="text-xs text-gray-400">Abonnement mensuel, trimestriel ou annuel</p>
              </div>
              <button
                onClick={() => setField('has_recurring', !form.has_recurring)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.has_recurring ? 'bg-[#1B99E5]' : 'bg-gray-200 dark:bg-gray-700'}`}
                data-testid="toggle-recurring"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.has_recurring ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {form.has_recurring && (
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs font-medium mb-1 block">Montant (€)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.recurring_amount}
                    onChange={e => setField('recurring_amount', e.target.value)}
                    className="h-8 text-sm"
                    data-testid="recurring-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Fréquence</Label>
                  <Select value={form.recurring_frequency} onValueChange={v => setField('recurring_frequency', v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensuel</SelectItem>
                      <SelectItem value="quarterly">Trimestriel</SelectItem>
                      <SelectItem value="annual">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Nombre de périodes</Label>
                  <Input
                    type="number" min="1"
                    value={form.recurring_count}
                    onChange={e => setField('recurring_count', e.target.value)}
                    className="h-8 text-sm"
                    data-testid="recurring-count"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Début des récurrents</Label>
                  <Input type="date" value={form.recurring_start_date} onChange={e => setField('recurring_start_date', e.target.value)} className="h-8 text-sm" />
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Récapitulatif financier</p>
            <div className="grid grid-cols-3 gap-2 text-sm mb-3">
              <div><p className="text-xs text-gray-400">Valeur contractuelle</p><p className="font-bold text-gray-900 dark:text-white">{formatCurrency(contractValue)}</p></div>
              <div><p className="text-xs text-gray-400">Acompte</p><p className="font-semibold text-amber-700">{form.has_deposit ? formatCurrency(actualDepositAmount) : '—'}</p></div>
              <div><p className="text-xs text-gray-400">Initial</p><p className="font-semibold text-indigo-700">{form.has_initial ? formatCurrency(form.initial_amount) : '—'}</p></div>
              <div><p className="text-xs text-gray-400">Total récurrents</p><p className="font-semibold text-purple-700">{form.has_recurring ? formatCurrency((parseFloat(form.recurring_amount) || 0) * (parseInt(form.recurring_count) || 0)) : '—'}</p></div>
              <div><p className="text-xs text-gray-400">Total échéancier</p><p className={`font-bold ${validationError ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>{formatCurrency(scheduleTotal)}</p></div>
              <div><p className="text-xs text-gray-400">Reste après acompte</p><p className="font-semibold text-gray-700 dark:text-gray-300">{form.has_deposit ? formatCurrency(contractValue - actualDepositAmount) : '—'}</p></div>
            </div>
            {validationError && (
              <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2.5">
                <AlertTriangle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700 dark:text-red-400">{validationError}</p>
              </div>
            )}
          </div>

          {/* Preview schedule */}
          {previewLines.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Prévisualisation — {previewLines.length} échéance{previewLines.length !== 1 ? 's' : ''}
              </p>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">#</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Type</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewLines.map((line, i) => {
                      const typeCfg = TYPE_CFG[line.type] || TYPE_CFG.initial;
                      return (
                        <tr key={i} className="border-t border-gray-50 dark:border-gray-800">
                          <td className="px-3 py-1.5 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-3 py-1.5">
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${typeCfg.cls}`}>
                              {typeCfg.label} {line.total > 1 ? `${line.num}/${line.total}` : ''}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">{formatDate(line.due_date)}</td>
                          <td className="px-3 py-1.5 text-xs font-semibold text-gray-900 dark:text-white text-right">{formatCurrency(line.amount)}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
                      <td colSpan={3} className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400">TOTAL</td>
                      <td className={`px-3 py-2 text-sm font-bold text-right ${validationError ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>{formatCurrency(scheduleTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Notes (optionnel)</Label>
            <Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2} placeholder="Conditions particulières, références..." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !!validationError}
            className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white"
            data-testid="create-plan-submit"
          >
            <Check size={14} className="mr-1.5" />
            {saving ? 'Création...' : 'Créer l\'échéancier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
